import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  Stack,
  Divider
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuthStore } from '../store/authStore';
import axiosInstance from '../store/api';
import { APPS } from '../utils/cloudStorage';
import { usePremiumPrice } from '../utils/premiumPricing';

export default function RenewSubscriptionModal({
  open,
  onClose,
  appName,
  appKey
}) {
  const user = useAuthStore(state => state.user);
  const app = APPS[appKey] || {};
  const isCrmSeats = appKey === 'corebase_crm_v1';
  const currentAccess = user?.apps?.[appKey] || user?.appsAccess?.[appKey];
  const currentSeats = Number(currentAccess?.additionalSellerSeats || 0);
  const [seatCount, setSeatCount] = useState(1);
  const [billingPeriod, setBillingPeriod] = useState('month');
  useEffect(() => { if (open) setSeatCount(Math.max(1, currentSeats)); }, [open, currentSeats]);
  const price = usePremiumPrice(app);
  const total = isCrmSeats ? seatCount * (billingPeriod === 'year' ? app.additionalSellerAnnualPrice : app.additionalSellerPrice) : price.total;
  const code = user?.personal_code || '';
  const phone = '59177858921';

  const copyCode = async () => {
    await navigator.clipboard.writeText(code);
    alert('Código copiado. Pégalo en la glosa del pago.');
  };

  const notifyPayment = async () => {
    try {
      await axiosInstance.post('/subscriptions/notify-payment', {
        appKey,
        personal_code: code,
        ...(isCrmSeats ? { additionalSellerSeats: seatCount, billingPeriod } : {})
      });

      alert(
        'Gracias 🙌\nTu pago fue registrado y será revisado en breve.'
      );
      onClose();
    } catch (e) {
      alert('Error al registrar el pago. Intenta nuevamente.');
    }
  };

  const whatsappMessage = encodeURIComponent(
    `Hola Jacob 👋\n\nYa realicé el pago de la app:\n` +
    `📦 ${appName} · USD ${total}/${isCrmSeats ? billingPeriod === 'year' ? 'año' : 'mes' : app.billingPeriod === 'year' ? 'año' : 'mes'}${isCrmSeats ? ` · ${seatCount} vendedores extra` : ''}\n\n` +
    `🆔 Código de usuario: ${code}\n\n` +
    `Adjunto mi comprobante para verificación.`
  );

  const whatsappLink = `https://wa.me/${phone}?text=${whatsappMessage}`;

  if (app.premium === false && !isCrmSeats) return <Dialog open={open} onClose={onClose}><DialogTitle>{appName} es gratis</DialogTitle><DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions></Dialog>;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCrmSeats ? 'Vendedores extra de CRM' : 'Renovar suscripción'}</DialogTitle>

      <DialogContent>
        {isCrmSeats ? <>
          <Typography mb={2}>CRM es gratis hasta 3 vendedores. Cada vendedor extra cuesta USD 1 al mes o USD 10 al año.</Typography>
          <TextField fullWidth type="number" label="Total de vendedores extra" value={seatCount} inputProps={{min:1,max:1000,step:1}} onChange={event => setSeatCount(Number(event.target.value))} sx={{mb:2}} />
          <TextField fullWidth select label="Período de los vendedores extra" value={billingPeriod} onChange={event=>setBillingPeriod(event.target.value)} sx={{mb:2}}><MenuItem value="month">Mensual</MenuItem><MenuItem value="year">Anual</MenuItem></TextField>
          <Typography fontWeight={700} mb={2}>Total: USD {Number.isFinite(total) ? total : 0}/{billingPeriod === 'year' ? 'año' : 'mes'}</Typography>
          <Typography variant="body2" mb={2}>Esta cantidad es el total de cupos extra del plan. Renovar la misma cantidad conserva el tiempo vigente. Si cambias la cantidad, el nuevo período empieza al aprobar el pago. Al vencer los extras, CRM sigue gratis con 3 vendedores.</Typography>
        </> : <Typography fontWeight={700} mb={2}>USD {total}/{app.billingPeriod === 'year' ? 'año' : 'mes'}</Typography>}
        <Typography mb={2}>
          Escanea el QR y realiza el pago desde tu app bancaria.
          <br />
          <strong>En la glosa del pago pega este código:</strong>
        </Typography>

        {/* QR */}
        <Box
          sx={{
            border: '1px dashed #ccc',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            mb: 3
          }}
        >
          <img
            src="/static/qr-pago.png"
            alt="QR de pago"
            style={{ width: 220, maxWidth: '100%' }}
          />
          <Typography variant="caption" display="block" mt={1}>
            QR de pago oficial
          </Typography>
        </Box>

        {/* Código */}
        <Stack direction="row" spacing={1} mb={2}>
          <TextField
            fullWidth
            label="Código para la glosa"
            value={code}
            InputProps={{ readOnly: true }}
          />
          <Button
            variant="outlined"
            onClick={copyCode}
            startIcon={<ContentCopyIcon />}
          >
            Copiar
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          ⚠️ Usa este código <strong>exactamente igual</strong> en la glosa del
          pago para identificar tu suscripción.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" mb={1}>
          📲 Luego del pago, envía el comprobante por WhatsApp a:
        </Typography>

        <Typography fontWeight="bold" mb={2}>
          +591 77858921 <br />
          Jacob Sanchez Bejarano
        </Typography>

        {/* WhatsApp */}
        <Button
          fullWidth
          variant="outlined"
          color="success"
          startIcon={<WhatsAppIcon />}
          href={whatsappLink}
          target="_blank"
          sx={{ mb: 2 }}
        >
          Enviar comprobante por WhatsApp
        </Button>

        <Typography variant="body2" color="text.secondary">
          La activación se realizará manualmente una vez verificado el pago.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: 'column',
          gap: 1,
          p: 2
        }}
      >
        {/* YA PAGUÉ */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<CheckCircleOutlineIcon />}
          disabled={isCrmSeats && (!Number.isInteger(seatCount) || seatCount < 1 || seatCount > 1000)}
          onClick={notifyPayment}
        >
          Ya pagué
        </Button>

        <Button fullWidth onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
